import { Controller, useForm } from 'react-hook-form';
import { Collapse, Flex, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import zodLocales from 'common/zod/locales';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import BooleanRadioGroup from 'features/Onboarding/components/BooleanRadioGroup';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';

import Header from '../../../../components/Header';
import locales from './locales';

type ResidencyDocumentProps = {
  onContinue: (idDocumentCase: z.infer<typeof residencyDocumentSchema>) => void;
};

const residencyDocumentSchema = z.discriminatedUnion(
  'ownedDocumentType',
  [
    z.object({
      ownedDocumentType: z.enum(['none', 'visa']),
    }),
    z.object({
      isResidencyCardValid: z.boolean({
        required_error: zodLocales.errors.required,
      }),
      ownedDocumentType: z.enum(['residencyCard']),
    }),
  ],
  {
    invalid_type_error: zodLocales.errors.required,
    required_error: zodLocales.errors.required,
  },
);

type ResidencyDocumentSchema = z.infer<typeof residencyDocumentSchema>;

const OPTIONS = [
  'residencyCard',
  'visa',
  'none',
] satisfies ResidencyDocumentSchema['ownedDocumentType'][];

const ResidencyDocument = asBentoPage<ResidencyDocumentProps>(
  ({ Layout, onContinue, onPrev }) => {
    const {
      control,
      formState: { errors },
      handleSubmit,
      watch,
    } = useForm<z.infer<typeof residencyDocumentSchema>>({
      mode: 'onChange',
      resolver: zodResolver(residencyDocumentSchema),
    });

    const onSubmit = handleSubmit((values) => {
      onContinue(values);
    });

    const ownedDocumentType = watch('ownedDocumentType');
    const isResidencyCardValid = watch('isResidencyCardValid');

    const hasNoValidResidencyCard =
      ownedDocumentType &&
      (ownedDocumentType !== 'residencyCard' || isResidencyCardValid === false);

    return (
      <Layout
        data-testid="residency-document"
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Header title={locales.title} />

          <Flex
            as="form"
            direction="column"
            height="full"
            justify="space-between"
            onSubmit={onSubmit}
          >
            <VStack alignItems="stretch" spacing="space-0">
              <Form.Field
                error={errors.ownedDocumentType?.message}
                label={locales.labels.ownedDocumentType}
                marginBottom="space-24"
              >
                <Controller
                  control={control}
                  name="ownedDocumentType"
                  render={({ field: { onChange, value } }) => (
                    <Form.Radio.Group
                      onChange={(val) => {
                        onChange(val);
                      }}
                      radioPosition="right"
                      value={value ?? undefined}
                      variant="outline"
                    >
                      {OPTIONS.map((option) => (
                        <Form.Radio
                          key={option}
                          padding="space-16"
                          value={option}
                        >
                          <Form.Radio.Title>
                            {locales.options[option]}
                          </Form.Radio.Title>
                        </Form.Radio>
                      ))}
                    </Form.Radio.Group>
                  )}
                />
              </Form.Field>

              <Collapse in={ownedDocumentType === 'residencyCard'}>
                <Form.Field
                  error={
                    'isResidencyCardValid' in errors
                      ? errors.isResidencyCardValid?.message
                      : undefined
                  }
                  label={locales.labels.isResidencyCardValid}
                  marginBottom="space-24"
                >
                  <Controller
                    control={control}
                    name="isResidencyCardValid"
                    render={({ field }) => (
                      <BooleanRadioGroup
                        onChange={field.onChange}
                        value={field.value}
                      />
                    )}
                  />
                </Form.Field>
              </Collapse>
            </VStack>

            {hasNoValidResidencyCard ? (
              <Callout variant="warning">
                <Callout.Content>{locales.noResidencyCard}</Callout.Content>
              </Callout>
            ) : null}

            <OBStickyButton justifyContent="flex-start" type="submit">
              {locales.continue}
            </OBStickyButton>
          </Flex>
        </Flex>
      </Layout>
    );
  },
);

export default ResidencyDocument;
